import { useState } from "react";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitToolLead } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function EmailResultsForm({
    toolSlug,
    resultSummary,
    zipCode,
}) {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [optIn, setOptIn] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !email.trim()) return;
        setSubmitting(true);
        try {
            await submitToolLead({
                tool_name: toolSlug,
                email: email.trim().toLowerCase(),
                first_name: firstName.trim() || null,
                marketing_opt_in: optIn,
                result_summary: resultSummary || null,
                zip_code: zipCode || null,
            });
            track("tool_email_results_submitted", {
                tool_name: toolSlug,
                opted_in: optIn,
                zip_present: !!zipCode,
            });
            setSent(true);
            toast.success("Sent! Check your inbox in a few moments.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "We couldn't send your results. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div
                className="bl-card p-6 border border-bl"
                data-testid="email-results-success"
            >
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--bl-moss)] mt-0.5" />
                    <div>
                        <h4 className="font-display font-semibold text-base text-bl">
                            We've queued your results.
                        </h4>
                        <p className="mt-1 text-sm text-bl-muted">
                            If you don't see it within a few minutes, check
                            spam, then{" "}
                            <a
                                href="/contact"
                                className="underline underline-offset-4"
                            >
                                let us know
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className="bl-card p-6 border border-bl"
            data-testid="email-results-form"
        >
            <div className="flex items-start gap-3 mb-4">
                <Mail className="h-5 w-5 text-bl-primary mt-0.5" />
                <div>
                    <h4 className="font-display font-semibold text-base text-bl">
                        Email me my results
                    </h4>
                    <p className="mt-1 text-xs text-bl-muted">
                        Optional. Your results stay visible on this page either
                        way.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                    type="text"
                    inputMode="text"
                    autoComplete="given-name"
                    placeholder="First name (optional)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bl-input"
                    data-testid="email-results-firstname"
                />
                <input
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bl-input"
                    data-testid="email-results-email"
                />
            </div>

            <label className="flex items-start gap-2 text-xs text-bl-muted cursor-pointer mb-4">
                <input
                    type="checkbox"
                    checked={optIn}
                    onChange={(e) => setOptIn(e.target.checked)}
                    className="mt-0.5 accent-[var(--bl-primary)]"
                    data-testid="email-results-optin"
                />
                <span>
                    Get strategic buyer insights, inspection tips, and homeowner
                    guidance delivered occasionally.
                </span>
            </label>

            <button
                type="submit"
                disabled={submitting}
                className="bl-btn-primary justify-center gap-2 w-full sm:w-auto"
                data-testid="email-results-submit"
            >
                {submitting ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                    </>
                ) : (
                    "Send my results"
                )}
            </button>
        </form>
    );
}
